/**
 * Canonical helpers for company interest fields.
 *
 * Two separate Airtable concepts (do not merge):
 * - InterestedOpportunities — working-capital preferences (positive CSV)
 * - Onboarding — onboarding wizard progress JSON, which carries grant/subsidization answers
 *
 * InterestedIn (grant names for Earnings Roadmap / PerfectDrafts) is unrelated.
 */

/** @deprecated Stored on older records — normalized on read. */
export const LEGACY_WORKING_CAPITAL_SOFTWARE_SAVINGS_LABEL = 'Tech/Cost Savings';

/** @deprecated Stored on older records — normalized to `0% loans` on read. */
export const LEGACY_WORKING_CAPITAL_ZERO_LOAN_LABEL =
  'Loans (interest-free or near interest-free)';

/** @deprecated Stored on older records — normalized to `Low-Interest Loans` on read. */
export const LEGACY_WORKING_CAPITAL_PG_LOAN_LABEL =
  'Loans (near interest-free with a personal guarantee)';

/** @deprecated Stored on older records — normalized to `Low-Interest Loans` on read. */
export const LEGACY_WORKING_CAPITAL_MARKET_LOAN_LABEL =
  'Loans (reasonable market rate)';

/** @deprecated Stored on older records — normalized to the expanded AI revenue label on read. */
export const LEGACY_WORKING_CAPITAL_AI_REVENUE_LABEL = 'AI Revenue Upgrades';

export const WORKING_CAPITAL_AI_REVENUE_LABEL =
  'AI Revenue Upgrades - AI BDR + AI Content Manager';

export const WORKING_CAPITAL_WARM_INTRODUCTIONS_LABEL = 'Warm Introductions';

export const WORKING_CAPITAL_LEGACY_LABEL_ALIASES = {
  [LEGACY_WORKING_CAPITAL_SOFTWARE_SAVINGS_LABEL]: 'Software Savings',
  [LEGACY_WORKING_CAPITAL_ZERO_LOAN_LABEL]: '0% loans',
  [LEGACY_WORKING_CAPITAL_PG_LOAN_LABEL]: 'Low-Interest Loans',
  [LEGACY_WORKING_CAPITAL_MARKET_LOAN_LABEL]: 'Low-Interest Loans',
  [LEGACY_WORKING_CAPITAL_AI_REVENUE_LABEL]: WORKING_CAPITAL_AI_REVENUE_LABEL,
};

export const WORKING_CAPITAL_INTEREST_OPTIONS = [
  'Software Savings',
  'Grants and Tax Credits',
  '0% loans',
  'Low-Interest Loans',
  'Equity Funding',
  'Automating Processes',
  WORKING_CAPITAL_AI_REVENUE_LABEL,
  WORKING_CAPITAL_WARM_INTRODUCTIONS_LABEL,
];

export const ONBOARDING_GRANT_INTEREST_OPTIONS = [
  'R&D',
  'Hiring',
  'Market Expansion',
  'Training & Talent Development',
  'Other',
];

export const GOVERNMENT_PROGRAMS_INTEREST_OPTIONS = [
  'Hiring - Talent - Training',
  'Market Expansion',
  'Technology Development',
  'Social Impact',
  'Niche/Industry-Specific',
  'Other',
];

/** Airtable long-text field holding the full onboarding wizard progress JSON. */
export const ONBOARDING_PROGRESS_FIELD = 'Onboarding';

/** Airtable field for working-capital interests (comma-separated positive labels). */
export const WORKING_CAPITAL_INTERESTS_FIELD = 'InterestedOpportunities';

const WORKING_CAPITAL_SET = new Set(WORKING_CAPITAL_INTEREST_OPTIONS);
const WORKING_CAPITAL_ACCEPTED_SET = new Set([
  ...WORKING_CAPITAL_INTEREST_OPTIONS,
  ...Object.keys(WORKING_CAPITAL_LEGACY_LABEL_ALIASES),
]);
const ONBOARDING_GRANT_SET = new Set(ONBOARDING_GRANT_INTEREST_OPTIONS);
const GOVERNMENT_PROGRAMS_SET = new Set(GOVERNMENT_PROGRAMS_INTEREST_OPTIONS);

export function normalizeWorkingCapitalLabel(label) {
  const trimmed = String(label ?? '').trim();
  return WORKING_CAPITAL_LEGACY_LABEL_ALIASES[trimmed] ?? trimmed;
}

/** Map stored labels (including legacy aliases) to canonical working-capital interests. */
export function normalizeWorkingCapitalInterests(values) {
  if (!Array.isArray(values)) return [];
  const seen = new Set();
  const result = [];
  for (const value of values) {
    const label = normalizeWorkingCapitalLabel(value);
    if (!WORKING_CAPITAL_SET.has(label) || seen.has(label)) continue;
    seen.add(label);
    result.push(label);
  }
  return result;
}

function asTrimmedList(values) {
  if (!Array.isArray(values)) return [];
  return [...new Set(values.map((v) => String(v).trim()).filter(Boolean))];
}

function tryParseJson(raw) {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
  try {
    let parsed = JSON.parse(trimmed);
    let depth = 0;
    while (typeof parsed === 'string' && depth < 5) {
      const next = parsed.trim();
      if (!next.startsWith('{') && !next.startsWith('[')) break;
      parsed = JSON.parse(next);
      depth += 1;
    }
    return parsed;
  } catch {
    return null;
  }
}

function splitDelimited(raw) {
  return String(raw || '')
    .split(/[,\n]/)
    .map((s) => s.trim().replace(/^"+|"+$/g, ''))
    .filter(Boolean);
}

/** True when a stored value looks like onboarding grant JSON (not WC interest CSV). */
export function isOnboardingGrantInterestsPayload(raw) {
  const parsed = tryParseJson(raw);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;

  if (parsed.expenses || parsed.otherLabel != null || parsed.otherTags) return true;

  if (Array.isArray(parsed.interests)) {
    const interests = asTrimmedList(parsed.interests);
    if (interests.length === 0) return true;
    return interests.some((label) => ONBOARDING_GRANT_SET.has(label));
  }

  return false;
}

export function emptyOnboardingGrantInterests() {
  return {
    interests: [],
    expenses: {},
    otherLabel: '',
    otherTags: [],
  };
}

/**
 * Parse a legacy onboarding grant/subsidization payload
 * (`{ interests, expenses, otherLabel, otherTags }`).
 */
export function parseOnboardingGrantInterests(raw) {
  const empty = emptyOnboardingGrantInterests();
  if (raw == null || raw === '') return empty;

  const parsed = tryParseJson(raw);
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const interests = asTrimmedList(parsed.interests)
      .map((label) =>
        ONBOARDING_GRANT_SET.has(label) ? label : normalizeWorkingCapitalLabel(label)
      )
      .filter((label) => ONBOARDING_GRANT_SET.has(label) || WORKING_CAPITAL_SET.has(label));
    const expenses =
      parsed.expenses && typeof parsed.expenses === 'object' && !Array.isArray(parsed.expenses)
        ? parsed.expenses
        : {};
    const otherLabel = parsed.otherLabel != null ? String(parsed.otherLabel) : '';
    const otherTags = asTrimmedList(parsed.otherTags);
    return { interests, expenses, otherLabel, otherTags };
  }

  if (Array.isArray(raw)) {
    return {
      ...empty,
      interests: asTrimmedList(raw)
        .map((label) =>
          ONBOARDING_GRANT_SET.has(label) ? label : normalizeWorkingCapitalLabel(label)
        )
        .filter((label) => ONBOARDING_GRANT_SET.has(label) || WORKING_CAPITAL_SET.has(label)),
    };
  }

  if (typeof raw === 'string') {
    return {
      ...empty,
      interests: splitDelimited(raw)
        .map((label) =>
          ONBOARDING_GRANT_SET.has(label) ? label : normalizeWorkingCapitalLabel(label)
        )
        .filter((label) => ONBOARDING_GRANT_SET.has(label) || WORKING_CAPITAL_SET.has(label)),
    };
  }

  return empty;
}

/**
 * Derive grant interests from the onboarding wizard progress JSON (companies.Onboarding).
 * Returns null when the payload carries no grant answers.
 */
export function deriveGrantInterestsFromOnboardingProgress(raw) {
  const parsed = tryParseJson(raw);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

  const formData =
    parsed.formData && typeof parsed.formData === 'object' && !Array.isArray(parsed.formData)
      ? parsed.formData
      : parsed;

  const interests = asTrimmedList(formData.grantInterests)
    .map((label) =>
      ONBOARDING_GRANT_SET.has(label) ? label : normalizeWorkingCapitalLabel(label)
    )
    .filter((label) => ONBOARDING_GRANT_SET.has(label) || WORKING_CAPITAL_SET.has(label));

  const expenses = {};
  Object.keys(formData).forEach((key) => {
    if (!key.startsWith('grantInterests_') || !key.endsWith('_expense')) return;
    const name = key.slice('grantInterests_'.length, -'_expense'.length);
    const rawValue = formData[key];
    const numeric =
      typeof rawValue === 'number'
        ? rawValue
        : parseFloat(String(rawValue ?? '').replace(/[^0-9.-]/g, '')) || 0;
    if (name && numeric > 0) expenses[name] = numeric;
  });

  const otherLabel =
    formData.grantInterests_Other_label != null ? String(formData.grantInterests_Other_label) : '';
  const otherTags = asTrimmedList(formData.grantInterests_Other_tags);

  const hasContent =
    interests.length > 0 ||
    Object.keys(expenses).length > 0 ||
    otherLabel.trim() !== '' ||
    otherTags.length > 0;

  return hasContent ? { interests, expenses, otherLabel, otherTags } : null;
}

/**
 * Load onboarding interests from company fields.
 * Order: Onboarding wizard progress JSON → legacy InterestedOpportunities JSON.
 */
export function loadOnboardingGrantInterestsFromCompanyFields(fields = {}) {
  const fromProgress = deriveGrantInterestsFromOnboardingProgress(
    fields?.[ONBOARDING_PROGRESS_FIELD]
  );
  if (fromProgress) return fromProgress;

  const legacy = fields?.[WORKING_CAPITAL_INTERESTS_FIELD];
  if (isOnboardingGrantInterestsPayload(legacy)) {
    return parseOnboardingGrantInterests(legacy);
  }

  return emptyOnboardingGrantInterests();
}

/**
 * Parse InterestedOpportunities as positive working-capital interests.
 * Onboarding grant JSON is ignored (returns []) so profile UI is not corrupted.
 * Legacy `{ optOut: [...] }` objects are converted to the complementary positive list.
 */
export function parseWorkingCapitalInterests(raw) {
  if (raw == null || raw === '') return [];
  if (isOnboardingGrantInterestsPayload(raw)) return [];

  if (Array.isArray(raw)) {
    return normalizeWorkingCapitalInterests(raw);
  }

  const parsed = tryParseJson(raw);
  if (Array.isArray(parsed)) {
    return normalizeWorkingCapitalInterests(parsed);
  }

  if (parsed && typeof parsed === 'object' && Array.isArray(parsed.optOut)) {
    const optOut = new Set(
      normalizeWorkingCapitalInterests(
        asTrimmedList(parsed.optOut).filter((label) => WORKING_CAPITAL_ACCEPTED_SET.has(label))
      )
    );
    return WORKING_CAPITAL_INTEREST_OPTIONS.filter((label) => !optOut.has(label));
  }

  if (parsed && typeof parsed === 'object' && Array.isArray(parsed.interests)) {
    return normalizeWorkingCapitalInterests(parsed.interests);
  }

  return normalizeWorkingCapitalInterests(splitDelimited(raw));
}

/** @deprecated Use parseWorkingCapitalInterests — kept for older opt-out callers. */
export function parseWorkingCapitalOptOut(raw) {
  const positive = new Set(parseWorkingCapitalInterests(raw));
  return WORKING_CAPITAL_INTEREST_OPTIONS.filter((label) => !positive.has(label));
}

/**
 * Positive working-capital interests.
 * - Empty / missing field → [] unless assumeAllWhenEmpty (emails)
 * - Onboarding JSON → only WC labels present in interests (legacy hybrids)
 * - Positive CSV / array → stored interests
 */
export function getPositiveWorkingCapitalInterests(raw, { assumeAllWhenEmpty = false } = {}) {
  if (raw == null || String(raw).trim() === '') {
    return assumeAllWhenEmpty ? [...WORKING_CAPITAL_INTEREST_OPTIONS] : [];
  }

  if (isOnboardingGrantInterestsPayload(raw)) {
    const { interests } = parseOnboardingGrantInterests(raw);
    return normalizeWorkingCapitalInterests(interests);
  }

  return parseWorkingCapitalInterests(raw);
}

/** Parse GovernmentProgramsInterest (comma-separated labels). */
export function parseGovernmentProgramsInterest(raw) {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return asTrimmedList(raw).filter((label) => GOVERNMENT_PROGRAMS_SET.has(label));
  }
  return splitDelimited(raw).filter((label) => GOVERNMENT_PROGRAMS_SET.has(label));
}

/** Serialize working-capital interests for Airtable (comma-separated positive labels). */
export function serializeWorkingCapitalInterests(interestList) {
  const cleaned = normalizeWorkingCapitalInterests(
    Array.isArray(interestList) ? interestList : splitDelimited(interestList)
  );
  return cleaned.length > 0 ? cleaned.join(', ') : null;
}

/** @deprecated Use serializeWorkingCapitalInterests. */
export function serializeWorkingCapitalOptOut(optOutList) {
  const optOut = new Set(
    normalizeWorkingCapitalInterests(
      asTrimmedList(optOutList).filter((label) => WORKING_CAPITAL_ACCEPTED_SET.has(label))
    )
  );
  return serializeWorkingCapitalInterests(
    WORKING_CAPITAL_INTEREST_OPTIONS.filter((label) => !optOut.has(label))
  );
}
